import {
  accessRequests,
  catalogFixtures,
  commercialAccess,
  mappingFixtures,
  mockCatalogSearch,
  platformSupplierIntegrationFixtures,
  supportGrants,
  tenantSupplierFixtures,
  tenantUsers,
} from '../mocks/fixtures.js';
import type { CatalogOffersResult, LoginInput, LoginResult, TenantAuthContext } from '../domain/models.js';
import type { DataSotaRepository } from './contracts.js';

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const wait = async <T>(value: T): Promise<T> => Promise.resolve(clone(value));

const mockAuthContext: TenantAuthContext = {
  user: { id: '1', displayName: 'Анна Круглова', email: 'owner@mosoboi.example' },
  tenant: { id: '1', slug: 'mosoboi', name: 'Мособои' },
  tenantRole: 'owner',
  tenantRoleLabel: 'Владелец',
  mfaVerified: true,
};

export class MockDataSotaRepository implements DataSotaRepository {
  readonly mode = 'mock' as const;
  private mfaPending = false;
  private authContext: TenantAuthContext | null = null;

  searchCatalog(query: string) { return wait(mockCatalogSearch(query)); }

  async loadCatalogOffers(catalogGuid: string): Promise<CatalogOffersResult> {
    const item = Object.values(catalogFixtures).flat().find((candidate) => candidate.id === catalogGuid);
    if (!item) return { catalogGuid, supplierCount: 0, warehouseCount: 0, offers: [], state: 'loaded' };
    return wait({
      catalogGuid,
      supplierCount: item.supplierCount,
      warehouseCount: item.warehouseCount,
      offers: item.offers,
      state: 'loaded' as const,
    });
  }

  async login(input: LoginInput): Promise<LoginResult> {
    if (!input.email || !input.password || input.email.includes('error')) throw new Error('Mock authentication failed.');
    if (input.email.includes('mfa')) {
      this.mfaPending = true;
      this.authContext = null;
      return { state: 'mfa_required', expiresAt: '2099-01-01T00:00:00Z' };
    }
    this.mfaPending = false;
    this.authContext = clone(mockAuthContext);
    return { state: 'authenticated', context: clone(mockAuthContext) };
  }

  async verifyMfa(code: string): Promise<TenantAuthContext> {
    if (!this.mfaPending || code === '000000') throw new Error('Mock MFA failed.');
    this.mfaPending = false;
    this.authContext = clone(mockAuthContext);
    return clone(mockAuthContext);
  }

  async restoreAuthContext(): Promise<TenantAuthContext> {
    this.authContext = clone(mockAuthContext);
    return clone(mockAuthContext);
  }

  async logout(): Promise<void> {
    this.authContext = null;
    this.mfaPending = false;
  }

  getCachedAuthContext(): TenantAuthContext | null {
    return this.authContext ? clone(this.authContext) : null;
  }

  listTenantSuppliers() { return wait(tenantSupplierFixtures); }
  listPlatformSupplierIntegrations() { return wait(platformSupplierIntegrationFixtures); }
  listMappings() { return wait(mappingFixtures); }
  listTenantUsers() { return wait(tenantUsers); }
  listAccessRequests() { return wait(accessRequests); }
  listCommercialAccess() { return wait(commercialAccess); }
  listSupportGrants() { return wait(supportGrants); }
}
